'use client';

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { projectPhoto } from '@/lib/projectPhoto';
import { PhotoPrompt } from '@/components/ui/NextVisual';
import SitePhoto from '@/components/ui/SitePhoto';
import { SITE_PHOTOS } from '@/data/sitePhotos';
import ScrollReveal from '@/app/components/ScrollReveal';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import CTAPrimary from '@/components/ui/CTAPrimary';
import CTASecondary from '@/components/ui/CTASecondary';
import { COMPANY } from '@/data/company';

// An architect-designed home we manufactured and built (Saral Mane — Ar. Ujwal Prabhu N, Nord Studio)
const ARCH_PHOTO = projectPhoto('saral-mane-g2-house-kanakapura-road', 'master-bedroom-dresser-with-lit-mirror-niche.jpg');

// ─── DATA ─────────────────────────────────────────────────────────────────────

const SUPPORT_ITEMS = [
  {
    id: '01',
    label: 'Manufacturing',
    sub: 'Modular components built to your specifications.',
  },
  {
    id: '02',
    label: 'Assembly',
    sub: 'Standardised processes. Consistent results.',
  },
  {
    id: '03',
    label: 'Quality Control',
    sub: 'Verified before anything reaches site.',
  },
  {
    id: '04',
    label: 'Installation',
    sub: 'Your design intent, executed on the ground.',
  },
];

const PROCESS_STEPS = [
  { label: 'Design Intent', note: 'Your drawings, specifications and intent.' },
  { label: 'Documentation', note: 'Translating design into manufacturing-ready documentation.' },
  { label: 'Manufacturing', note: 'Modular components produced with precision.' },
  { label: 'QC', note: 'Finish, fit and function verified before dispatch.' },
  { label: 'Installation', note: 'Supervised on-site execution.' },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

// `journey`: the next-visual cards for this page, built on the server (see src/data/journeyItems.ts)
export default function ArchitectsContent({ journey }: { journey?: React.ReactNode } = {}) {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div
      className="bg-warm-white min-h-screen"
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Professional Partnerships', href: '/b2b' },
            { label: 'Architects' },
          ]}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — HERO
          Technical grid: full-bleed image with overlaid text block
          Architectural, blueprint-like feel
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="arch-h1" className="overflow-hidden">
        {/* Outer wrapper — dark background for the grid lines */}
        <div
          className="relative"
          style={{
            backgroundColor: 'var(--deep)',
            minHeight: '90vh',
          }}
        >
          {/* Subtle grid overlay — architectural blueprint feel */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(170,125,83,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(170,125,83,0.06) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />

          {/* Content grid */}
          <div className="container-willow relative z-10">
            <div className="grid lg:grid-cols-[1fr_1fr] min-h-[90vh]">

              {/* Left — text block, vertically centered */}
              <div className="flex flex-col justify-center py-20 lg:py-28 lg:pr-16">

                {/* Label */}
                <div className="flex items-center gap-3 mb-10">
                  <span
                    style={{
                      display: 'block',
                      width: '2rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    FOR ARCHITECTS
                  </span>
                </div>

                {/* The question */}
                <blockquote
                  style={{
                    fontSize: 'clamp(0.85rem, 1.4vw, 1rem)',
                    lineHeight: 1.65,
                    color: 'rgba(250,248,245,0.45)',
                    fontStyle: 'italic',
                    borderLeft: '1px solid rgba(170,125,83,0.35)',
                    paddingLeft: '1.25rem',
                    marginBottom: '2.5rem',
                    maxWidth: '28rem',
                  }}
                >
                  "I have a project that requires interior manufacturing and execution. Is there a manufacturing partner I can work with?"
                </blockquote>

                {/* Core headline — stacked, architectural */}
                <h1
                  id="arch-h1"
                  style={{
                    fontSize: 'clamp(2.8rem, 6.5vw, 5.5rem)',
                    fontWeight: 800,
                    lineHeight: 0.95,
                    letterSpacing: '-0.045em',
                    color: '#FAF8F5',
                    marginBottom: '0.15em',
                  }}
                >
                  {/* Topic line inside the H1 so the heading says what the page is about; slogan below */}
                  <span className="block text-section-label mb-4 leading-normal">Manufacturing &amp; Execution Partner for Architects</span>
                  YOUR DESIGN
                </h1>
                <h2
                  style={{
                    fontSize: 'clamp(2.8rem, 6.5vw, 5.5rem)',
                    fontWeight: 800,
                    lineHeight: 0.95,
                    letterSpacing: '-0.045em',
                    color: 'var(--primary)',
                    marginBottom: '2rem',
                  }}
                >
                  INTENT.
                </h2>
                <h2
                  style={{
                    fontSize: 'clamp(2.8rem, 6.5vw, 5.5rem)',
                    fontWeight: 800,
                    lineHeight: 0.95,
                    letterSpacing: '-0.045em',
                    color: '#FAF8F5',
                    marginBottom: '0.15em',
                  }}
                >
                  OUR MANU-
                </h2>
                <h2
                  style={{
                    fontSize: 'clamp(2.8rem, 6.5vw, 5.5rem)',
                    fontWeight: 800,
                    lineHeight: 0.95,
                    letterSpacing: '-0.045em',
                    color: '#FAF8F5',
                    marginBottom: '2.5rem',
                  }}
                >
                  FACTURING<br />
                  <span style={{ color: 'var(--primary)' }}>EXPERTISE.</span>
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                    lineHeight: 1.7,
                    color: 'rgba(250,248,245,0.65)',
                    maxWidth: '28rem',
                    marginBottom: '2.5rem',
                  }}
                >
                  Willow supports architects and architectural practices with modular manufacturing and interior execution. Bring your design intent — we provide the production infrastructure to build it.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <CTAPrimary label="Discuss a Project" href="/contact" />
                  <CTASecondary label="View Our Work" href="/projects" />
                </div>
              </div>

              {/* Right — image, flush, with coordinate annotation */}
              <div className="relative hidden lg:block">
                <SitePhoto photo={SITE_PHOTOS.studioBarCounter} aspectRatio="2/3" className="w-full h-full absolute inset-0" priority />
                {/* Technical annotation overlay */}
                <div
                  className="absolute top-8 right-8 flex flex-col gap-1"
                  aria-hidden="true"
                >
                  <span
                    style={{
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      letterSpacing: '0.2em',
                      color: 'rgba(170,125,83,0.7)',
                      textTransform: 'uppercase',
                    }}
                  >
                    WILLOW · MANUFACTURING
                  </span>
                  <span
                    style={{
                      fontSize: '0.55rem',
                      letterSpacing: '0.12em',
                      color: 'rgba(250,248,245,0.3)',
                    }}
                  >
                    BENGALURU, INDIA
                  </span>
                </div>
                {/* Corner bracket — bottom left */}
                <div
                  className="absolute bottom-8 left-8"
                  aria-hidden="true"
                  style={{
                    width: '2rem',
                    height: '2rem',
                    borderLeft: '1px solid rgba(170,125,83,0.5)',
                    borderBottom: '1px solid rgba(170,125,83,0.5)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — WHY WILLOW
          Light background, asymmetric text + thin rule composition
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="why-willow-arch" className="border-b border-border">
        <ScrollReveal>
          <div className="container-willow py-20 md:py-28">
            {/* Top rule with label */}
            <div className="flex items-center gap-6 mb-16">
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  color: 'var(--primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                WHY WILLOW
              </span>
              <div
                style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'var(--border)',
                }}
                aria-hidden="true"
              />
            </div>

            {/* Asymmetric two-column */}
            <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-start">
              <div>
                <h2
                  id="why-willow-arch"
                  style={{
                    fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                    fontWeight: 700,
                    lineHeight: 1.08,
                    letterSpacing: '-0.04em',
                    color: 'var(--foreground)',
                  }}
                >
                  Manufacturing capability built from real interior projects.
                </h2>
              </div>

              <div className="flex flex-col gap-6">
                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
                    lineHeight: 1.72,
                    color: 'var(--secondary)',
                  }}
                >
                  Willow began with manufacturing. Our production experience became one of the foundations of how we approach interior execution — and it's what allows us to work effectively with architects who already have a clear design intent.
                </p>
                <p
                  style={{
                    fontSize: 'clamp(0.9rem, 1.4vw, 1rem)',
                    lineHeight: 1.72,
                    color: 'var(--secondary)',
                    opacity: 0.75,
                  }}
                >
                  For an architect, that means a partner who can read your documentation, understand your intent, and execute it through a controlled manufacturing and installation process — without requiring you to redesign around production constraints.
                </p>

                {/* Capability strip */}
                <div
                  className="flex flex-wrap gap-x-5 gap-y-2 pt-5"
                  style={{ borderTop: '1px solid var(--border)' }}
                >
                  {[
                    'Modular Manufacturing',
                    'Edge Banding',
                    'Laminate Work',
                    'Assembly',
                    'QC',
                    'Installation',
                  ]?.map((cap) => (
                    <span
                      key={cap}
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--secondary)',
                        opacity: 0.6,
                      }}
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — WHAT WE SUPPORT
          Dark background, large typographic list — architectural, minimal
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="what-we-support-heading"
        style={{ backgroundColor: 'var(--deep)' }}
      >
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="flex items-center gap-6 mb-16">
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  color: 'var(--primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                WHAT WE SUPPORT
              </span>
              <div
                style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(250,248,245,0.1)',
                }}
                aria-hidden="true"
              />
            </div>
          </ScrollReveal>

          {/* Large typographic list — each item is a full-width row */}
          <div>
            {SUPPORT_ITEMS?.map((item, idx) => (
              <ScrollReveal key={item?.id}>
                <div
                  style={{
                    borderTop: idx === 0 ? '1px solid rgba(250,248,245,0.08)' : 'none',
                    borderBottom: '1px solid rgba(250,248,245,0.08)',
                    padding: '1.5rem 0',
                  }}
                >
                  <div className="grid grid-cols-[2.5rem_1fr] lg:grid-cols-[2.5rem_1fr_1fr] gap-4 lg:gap-8 items-center">
                    {/* Index */}
                    <span
                      style={{
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        letterSpacing: '0.18em',
                        color: 'var(--primary)',
                        opacity: 0.7,
                      }}
                    >
                      {item?.id}
                    </span>

                    {/* Label */}
                    <h3
                      id={idx === 0 ? 'what-we-support-heading' : undefined}
                      style={{
                        fontSize: 'clamp(1.6rem, 4vw, 3rem)',
                        fontWeight: 700,
                        letterSpacing: '-0.04em',
                        lineHeight: 1.05,
                        color: '#FAF8F5',
                      }}
                    >
                      {item?.label}
                    </h3>

                    {/* Sub — desktop only */}
                    <p
                      className="hidden lg:block"
                      style={{
                        fontSize: '0.9rem',
                        lineHeight: 1.65,
                        color: 'rgba(250,248,245,0.45)',
                        maxWidth: '22rem',
                      }}
                    >
                      {item?.sub}
                    </p>
                  </div>

                  {/* Sub — mobile */}
                  <p
                    className="lg:hidden mt-2"
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: 1.65,
                      color: 'rgba(250,248,245,0.45)',
                      paddingLeft: 'calc(2.5rem + 1rem)',
                    }}
                  >
                    {item?.sub}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Modular products — compact, bottom of section */}
          <ScrollReveal>
            <div
              className="mt-14 flex flex-wrap gap-3"
              style={{ borderTop: '1px solid rgba(250,248,245,0.08)', paddingTop: '1.5rem' }}
            >
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'rgba(250,248,245,0.3)',
                  width: '100%',
                  marginBottom: '0.5rem',
                }}
              >
                Products we manufacture
              </span>
              {['Kitchens', 'Wardrobes', 'TV Units', 'Crockery Units', 'Beds', 'Vanities']?.map((p) => (
                <span
                  key={p}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    color: 'rgba(250,248,245,0.5)',
                    border: '1px solid rgba(250,248,245,0.12)',
                    padding: '0.35rem 0.85rem',
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 4 — HOW IT WORKS
          Light stone background, horizontal process chain
          Technical/diagrammatic — not a card grid
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="how-it-works-arch"
        style={{ backgroundColor: 'var(--stone)' }}
      >
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="flex items-center gap-6 mb-16">
              <h2
                id="how-it-works-arch"
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  color: 'var(--primary)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.5,
                }}
              >
                HOW IT WORKS
              </h2>
              <div
                style={{ flex: 1, height: '1px', backgroundColor: 'var(--border)' }}
                aria-hidden="true"
              />
            </div>
          </ScrollReveal>

          {/* Desktop: horizontal chain with interactive step detail */}
          <div className="hidden lg:block">
            {/* Step labels + connector line */}
            <ScrollReveal>
              <div className="relative mb-12">
                {/* Connector line */}
                <div
                  aria-hidden="true"
                  className="absolute top-5 left-0 right-0"
                  style={{ height: '1px', backgroundColor: 'var(--border)' }}
                />
                <div className="grid grid-cols-5 gap-4">
                  {PROCESS_STEPS?.map((step, idx) => (
                    <button
                      key={step?.label}
                      onClick={() => setActiveStep(idx)}
                      className="flex flex-col items-start gap-3 text-left group"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      aria-pressed={activeStep === idx}
                    >
                      {/* Dot */}
                      <div
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: activeStep === idx ? 'var(--primary)' : 'var(--border)',
                          border: activeStep === idx ? '2px solid var(--primary)' : '2px solid var(--border)',
                          transition: 'all 0.2s ease',
                          marginTop: '0',
                          position: 'relative',
                          zIndex: 1,
                          flexShrink: 0,
                        }}
                      />
                      {/* Step number */}
                      <span
                        style={{
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          letterSpacing: '0.18em',
                          color: activeStep === idx ? 'var(--primary)' : 'var(--secondary)',
                          opacity: activeStep === idx ? 1 : 0.5,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {String(idx + 1)?.padStart(2, '0')}
                      </span>
                      {/* Label */}
                      <span
                        style={{
                          fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
                          fontWeight: activeStep === idx ? 700 : 500,
                          letterSpacing: '-0.01em',
                          color: activeStep === idx ? 'var(--foreground)' : 'var(--secondary)',
                          transition: 'all 0.2s ease',
                          lineHeight: 1.2,
                        }}
                      >
                        {step?.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* Active step detail */}
            <ScrollReveal>
              <div
                style={{
                  borderLeft: '2px solid var(--primary)',
                  paddingLeft: '1.5rem',
                  minHeight: '3rem',
                }}
              >
                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.8vw, 1.3rem)',
                    lineHeight: 1.65,
                    color: 'var(--secondary)',
                    maxWidth: '36rem',
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {PROCESS_STEPS?.[activeStep]?.note}
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* Mobile: vertical stacked steps */}
          <div className="lg:hidden flex flex-col gap-0">
            {PROCESS_STEPS?.map((step, idx) => (
              <div
                key={step?.label}
                style={{
                  display: 'flex',
                  gap: '1.25rem',
                  paddingBottom: idx < PROCESS_STEPS?.length - 1 ? '1.5rem' : 0,
                }}
              >
                {/* Left: dot + line */}
                <div className="flex flex-col items-center" style={{ width: '10px', flexShrink: 0 }}>
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary)',
                      flexShrink: 0,
                    }}
                  />
                  {idx < PROCESS_STEPS?.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        width: '1px',
                        backgroundColor: 'var(--border)',
                        marginTop: '4px',
                      }}
                    />
                  )}
                </div>
                {/* Right: content */}
                <div style={{ paddingBottom: '0.5rem' }}>
                  <p
                    style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.18em',
                      color: 'var(--primary)',
                      marginBottom: '0.3rem',
                    }}
                  >
                    {String(idx + 1)?.padStart(2, '0')}
                  </p>
                  <h3
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      color: 'var(--foreground)',
                      marginBottom: '0.35rem',
                    }}
                  >
                    {step?.label}
                  </h3>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: 1.65,
                      color: 'var(--secondary)',
                    }}
                  >
                    {step?.note}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 5 — PROJECTS / PROOF
          Asymmetric image grid — let the work speak
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="projects-proof-arch" className="border-t border-border">
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
              <div className="flex items-center gap-6">
                <span
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    color: 'var(--primary)',
                  }}
                >
                  THE WORK
                </span>
                <div
                  style={{ width: '3rem', height: '1px', backgroundColor: 'var(--border)' }}
                  aria-hidden="true"
                />
              </div>
              <Link
                href="/projects"
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                All Projects <span aria-hidden="true">→</span>
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <h2
              id="projects-proof-arch"
              style={{
                fontSize: 'clamp(1.6rem, 3.5vw, 2.5rem)',
                fontWeight: 700,
                letterSpacing: '-0.04em',
                lineHeight: 1.1,
                color: 'var(--foreground)',
                maxWidth: '28rem',
                marginBottom: '3rem',
              }}
            >
              Real projects.<br />Real execution.
            </h2>
          </ScrollReveal>

          {/* Asymmetric image grid — 2 tall + 1 wide */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4">
            {/* Left: tall image */}
            <ScrollReveal>
              <div className="relative">
                <SitePhoto photo={ARCH_PHOTO} aspectRatio="4/5" sizes="(min-width: 768px) 45vw, 100vw" />
                <PhotoPrompt href={ARCH_PHOTO.projectHref} className="mt-3">
                  {ARCH_PHOTO.projectName}, designed by Ar. Ujwal Prabhu N — see the full project
                </PhotoPrompt>
              </div>
            </ScrollReveal>

            {/* Right: two stacked images */}
            <div className="flex flex-col gap-4">
              <ScrollReveal>
                <div style={{ aspectRatio: '4/3' }}>
                  <SitePhoto photo={SITE_PHOTOS.edgeBander} aspectRatio="4/3" className="w-full h-full" />
                </div>
              </ScrollReveal>
              <ScrollReveal>
                <div style={{ aspectRatio: '4/3' }}>
                  <SitePhoto photo={SITE_PHOTOS.sitePanels} aspectRatio="4/3" className="w-full h-full" />
                </div>
              </ScrollReveal>
            </div>
          </div>

          <ScrollReveal>
            <p
              style={{
                fontSize: '0.8rem',
                lineHeight: 1.65,
                color: 'var(--secondary)',
                opacity: 0.55,
                marginTop: '1.5rem',
                fontStyle: 'italic',
              }}
            >
              Real Willow project imagery will be placed here. Placeholders shown above.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── NEXT VISUALS — where to look next (visual journey) ── */}
      {journey}

      {/* ══════════════════════════════════════════════════════════
          SECTION 6 — CTA
          Dark, full-width, architectural closing
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="arch-cta-heading"
        style={{ backgroundColor: 'var(--deep)' }}
      >
        <ScrollReveal>
          <div className="container-willow py-24 md:py-32">
            <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-end">

              {/* Left: copy */}
              <div>
                {/* Rule + label */}
                <div className="flex items-center gap-4 mb-10">
                  <div
                    style={{ width: '2rem', height: '1px', backgroundColor: '#AA7D53' }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    START THE CONVERSATION
                  </span>
                </div>

                <h2
                  id="arch-cta-heading"
                  style={{
                    fontSize: 'clamp(2rem, 5vw, 4rem)',
                    fontWeight: 800,
                    lineHeight: 0.97,
                    letterSpacing: '-0.045em',
                    color: '#FAF8F5',
                    marginBottom: '1.5rem',
                  }}
                >
                  Have a project that needs<br />
                  <span style={{ color: 'var(--primary)' }}>manufacturing or execution</span><br />
                  support?
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
                    lineHeight: 1.7,
                    color: 'rgba(250,248,245,0.55)',
                    maxWidth: '30rem',
                  }}
                >
                  Tell us about the project. We'll start by understanding the scope and your design intent.
                </p>
              </div>

              {/* Right: CTAs */}
              <div className="flex flex-col gap-3 lg:items-end">
                <CTAPrimary label="Let's Talk" href="/contact" />
                <a
                  href={COMPANY?.whatsapp?.message}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(250,248,245,0.45)',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                  }}
                >
                  <span aria-hidden="true">↗</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
